import React from "react";

const RATING_CATEGORIES = [
  {
    label: "Quality of products delivered",
    keys: [
      "product_quality_points",
      "product_quality_rating",
      "productQualityPoints",
      "productQualityRating",
      "quality",
      "quality_points",
      "quality_rating",
    ],
  },
  {
    label: "Delivered service",
    keys: [
      "user_rating_points",
      "userRatingPoints",
      "rating",
      "service",
      "service_points",
      "delivered_service_points",
    ],
  },
  {
    label: "Packaging goods",
    keys: [
      "packaging_goods_points",
      "packaging_goods_rating",
      "packagingGoodsPoints",
      "packagingGoodsRating",
      "packaging",
      "packaging_points",
      "packaging_rating",
    ],
  },
  {
    label: "Products Accuracy",
    keys: [
      "product_accuracy_points",
      "product_accuracy_rating",
      "productAccuracyPoints",
      "productAccuracyRating",
      "accuracy",
      "accuracy_points",
      "accuracy_rating",
    ],
  },
];

const SUMMARY_ROWS = [
  {
    label: "Item Total",
    keys: ["cart_amount", "cartAmount", "item_total", "itemTotal", "sub_total", "subtotal"],
  },
  {
    label: "Service Charges (2.50 %)",
    keys: ["time_charges", "timeCharges", "service_charges", "serviceCharges"],
  },
  {
    label: "Delivery Charges",
    keys: ["delivery_amount", "deliveryAmount", "delivery_charges", "deliveryCharges"],
  },
  {
    label: "Coupon",
    keys: ["discount_amount", "discountAmount", "coupon", "coupon_amount", "couponAmount"],
  },
  {
    label: "Tip Amount",
    keys: ["tip_amount", "tipAmount", "tip", "driver_tip", "driverTip"],
  },
];

const COMMENT_KEYS = [
  "user_comment",
  "user_comments",
  "userComment",
  "userComments",
  "comment",
  "review",
  "feedback",
  "message",
];

const ORDER_NOTE_KEYS = [
  "review_order_text",
  "order_review_note",
  "order_review_text",
  "past_order_note",
];

const IMAGE_KEYS = [
  "images",
  "review_images",
  "rating_images",
  "image",
  "image_one",
  "rating_image_one",
  "image_two",
  "rating_image_two",
  "image_three",
  "rating_image_three",
  "image_four",
  "rating_image_four",
  "image_five",
  "rating_image_five",
];

const IMAGE_SOURCE_KEYS = [
  "url",
  "src",
  "image",
  "image_url",
  "imageUrl",
  "full_url",
  "path",
  "thumbnail",
  "preview",
];

const isPlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value);

const hasValue = (value) => value !== undefined && value !== null && value !== "";

const parseJsonValue = (value) => {
  if (typeof value !== "string") return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return value;
  }
};

const getFirstValue = (sources, keys, fallback = "") => {
  for (const source of sources) {
    if (!isPlainObject(source)) continue;

    const matchedKey = keys.find((key) => hasValue(source?.[key]));
    if (matchedKey) return source[matchedKey];
  }

  return fallback;
};

const getRatingsPayload = (ordersDetail) => {
  const ratings = parseJsonValue(ordersDetail?.ratings);

  if (isPlainObject(ratings)) return ratings;

  if (Array.isArray(ratings)) {
    return ratings.map(parseJsonValue).find(isPlainObject) || null;
  }

  return null;
};

const getReceiptPayload = (ordersDetail) => {
  const receipt = parseJsonValue(ordersDetail?.receipt);

  return isPlainObject(receipt) ? receipt : {};
};

const getRatingSources = (ordersDetail) => {
  const ratingsPayload = getRatingsPayload(ordersDetail);

  if (!ratingsPayload) return [];

  return [
    ratingsPayload,
    ratingsPayload.data,
    ratingsPayload.ratings,
    ratingsPayload.rating_data,
    ratingsPayload.ratingData,
    ratingsPayload.order_rating,
    ratingsPayload.orderRating,
    ratingsPayload.order_review,
    ratingsPayload.orderReview,
    ordersDetail,
  ].filter(isPlainObject);
};

const normalizeRating = (value) => {
  const rating = Number(value);

  if (!Number.isFinite(rating) || rating <= 0) return 0;

  return Math.min(Math.max(rating, 0), 5);
};

const getImageSource = (value) => {
  const parsedValue = parseJsonValue(value);

  if (typeof parsedValue === "string") {
    return parsedValue.trim();
  }

  if (!isPlainObject(parsedValue)) return "";

  return getFirstValue([parsedValue], IMAGE_SOURCE_KEYS, "");
};

const normalizeImageList = (value) => {
  const parsedValue = parseJsonValue(value);

  if (Array.isArray(parsedValue)) {
    return parsedValue.flatMap(normalizeImageList);
  }

  if (isPlainObject(parsedValue)) {
    const source = getImageSource(parsedValue);

    if (source) return [source];

    return Object.values(parsedValue).flatMap(normalizeImageList);
  }

  if (typeof parsedValue === "string") {
    const trimmedValue = parsedValue.trim();

    if (!trimmedValue) return [];

    if (trimmedValue.includes(",") && !trimmedValue.startsWith("data:")) {
      return trimmedValue
        .split(",")
        .map((image) => image.trim())
        .filter(Boolean);
    }

    return [trimmedValue];
  }

  return [];
};

const getReviewImages = (sources) => {
  const imageValues = [];

  sources.forEach((source) => {
    IMAGE_KEYS.forEach((key) => {
      if (hasValue(source?.[key])) {
        imageValues.push(source[key]);
      }
    });
  });

  return [...new Set(imageValues.flatMap(normalizeImageList).filter(Boolean))];
};

const getOrderRatingDetail = (ordersDetail) => {
  const sources = getRatingSources(ordersDetail);

  if (!sources.length) return null;

  const categories = RATING_CATEGORIES.map((category) => ({
    ...category,
    value: normalizeRating(getFirstValue(sources, category.keys, 0)),
  })).filter((category) => category.value > 0);
  const comment = String(getFirstValue(sources, COMMENT_KEYS, "") || "").trim();
  const images = getReviewImages(sources);
  const hasRatings = categories.length > 0;

  if (!hasRatings && !comment && images.length === 0) return null;

  return {
    categories,
    comment,
    hasRatings,
    images,
  };
};

const RatingStars = ({ value }) => {
  const filledStars = Math.round(value);

  return (
    <div className="order-rating-stars" aria-label={`${value} out of 5`}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          className={`order-rating-star ${star <= filledStars ? "is-filled" : ""}`}
          aria-hidden="true"
        >
          ★
        </span>
      ))}
    </div>
  );
};

const formatMoney = (currency, value) => {
  if (!hasValue(value)) return "";

  return currency ? `${currency} ${value}` : String(value);
};

const formatPromoValue = (value) => {
  if (!hasValue(value)) return "";

  const textValue = String(value).trim();
  if (/off$/i.test(textValue)) return textValue;

  return `${textValue} OFF`;
};

const SummaryRow = ({ label, value, currency }) => {
  if (!hasValue(value)) return null;

  return (
    <div className="summary-row">
      <span>{label}</span>
      <span>{formatMoney(currency, value)}</span>
    </div>
  );
};

const OrderSummary = ({ordersDetail}) => {
  const receipt = getReceiptPayload(ordersDetail);
  const summarySources = [receipt, ordersDetail].filter(isPlainObject);
  const ratingsPayload = getRatingsPayload(ordersDetail);
  const showRatingSection = Boolean(ratingsPayload);
  const currency = getFirstValue(summarySources, ["currency"], ordersDetail?.currency || "INR");
  const grandTotal = getFirstValue(summarySources, [
    "total_amount",
    "totalAmount",
    "grand_total",
    "grandTotal",
  ]);
  const tipAmount = getFirstValue(summarySources, [
    "tip_amount",
    "tipAmount",
    "tip",
    "driver_tip",
    "driverTip",
  ], 0);
  const reviewOrderText =
    getFirstValue(summarySources, ORDER_NOTE_KEYS) ||
    "Order can be done by past order details at any time.";
  const promoValue = getFirstValue(summarySources, [
    "promo_discount",
    "promoDiscount",
    "promo_code_discount",
    "promoCodeDiscount",
    "coupon_discount",
    "couponDiscount",
    "coupon_amount",
    "couponAmount",
    "discount_amount",
    "discountAmount",
  ]);
  const ratingDetail = getOrderRatingDetail(ordersDetail);

  return (
    <>
    <div className="card mt-10 order-summary order-summary-card">

      <div className="card-body">
        <h6 className="fw-bold mb-3">Order Details</h6>

        {hasValue(promoValue) && Number(promoValue) !== 0 && (
          <div className="order-summary-promo-row">
            <span className="order-summary-promo-label">
              <i className="ph ph-check-circle" aria-hidden="true" />
              Promo code applied
            </span>
            <span className="order-summary-promo-value">{formatPromoValue(promoValue)}</span>
          </div>
        )}

        {SUMMARY_ROWS.map((row) => (
          <SummaryRow
            key={row.label}
            label={row.label}
            value={getFirstValue(summarySources, row.keys)}
            currency={currency}
          />
        ))}

        <hr />

        <div className="summary-row grand-total">
          <span>Grand Total</span>
          <span>{formatMoney(currency, grandTotal)}</span>
        </div>

        {showRatingSection && (
          <>
            <div className="order-summary-extra">
              <div className="order-summary-tip">
                <p className="order-summary-tip-title">Do you want to leave a tip?</p>
                <small>100% of the tip goes to the driver</small>

                <div className="order-summary-tip-box">
                  <span className="order-summary-tip-currency">{currency}</span>
                  <div className="order-summary-tip-counter">
                    <button type="button" aria-label="Decrease tip amount" disabled>-</button>
                    <span>{tipAmount}</span>
                    <button type="button" aria-label="Increase tip amount" disabled>+</button>
                  </div>
                </div>
              </div>

              {reviewOrderText && (
                <div className="order-summary-note">
                  {reviewOrderText}
                </div>
              )}
            </div>

            {ratingDetail && (
              <div className="order-rating-summary">
                {ratingDetail.comment && (
                  <div className="order-rating-comment">
                    <span className="order-rating-title">Add comment if any</span>
                    <p className="order-rating-comment-text">{ratingDetail.comment}</p>
                  </div>
                )}

                {ratingDetail.hasRatings && (
                  <div className="order-rating-list">
                    {ratingDetail.categories.map((item) => (
                      <div className="order-rating-item" key={item.label}>
                        <span className="order-rating-label">{item.label}</span>
                        <RatingStars value={item.value} />
                      </div>
                    ))}
                  </div>
                )}

                {ratingDetail.images.length > 0 && (
                  <div className="order-rating-images">
                    <span className="order-rating-title">Upload images (Optional)</span>
                    <div className="order-rating-images-grid">
                      {ratingDetail.images.map((image, index) => (
                        <div className="order-rating-image" key={`${image}-${index}`}>
                          <img src={image} alt={`Order review ${index + 1}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      </div>     
    </>
  );
};

export default OrderSummary;
